import React, { Fragment } from 'react';
import { Button, Link, Tooltip } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import { cloneDeep, findIndex, forEach, get, lowerCase, map, startCase } from 'lodash';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { getStatusColor } from '../../Api/List/utils';
import { getI18nFormattedMessage } from '../../../utils/intl';
import EditIcon from "@material-ui/icons/Edit";
import { AUTH_METHOD_SECRET, AUTH_METHOD_NONE, ROLE_ORG_ADMIN_UUID, USER_LIST_ROLE } from "../../../constants";
import moment from 'moment';
import { capitalCase } from 'change-case';

export const buildSingleApplicationRequestMap = (classes, requestDetails) => {
  const originalData = requestDetails.original;
  const updateRequest = requestDetails.updateRequest;
  const requestSubtype = requestDetails.request.requestSubtype;
  let requestAppDetails, requestCustomFields, requestApis,
    requestApiGroups, requestProducts;
  if (updateRequest) {
    requestAppDetails = updateRequest.applicationDetails;
    requestCustomFields = updateRequest.applicationCustomFields;
    requestApis = updateRequest.applicationApis;
    requestApiGroups = updateRequest.applicationApiGroups;
    requestProducts = updateRequest.applicationProducts;
  }

  const returnMap = { "details": buildDetailsData(classes, originalData.applicationDetails, requestAppDetails) };
  const customFields = originalData.applicationCustomFields;
  if (customFields && customFields.length > 0) {
    returnMap['customfields'] = buildCustomFieldsData(customFields, requestCustomFields);
  }
  const apis = originalData.applicationApis;
  if (requestApis) {
    const type = requestDetails.request.requestType === "APPLICATION_API_PLANS" ? "apiplans" : "apis";
    returnMap['apis'] = buildApisCompareTable(apis, requestApis, type);
  } else if (apis && apis.length > 0) {
    returnMap['apis'] = apis;
  }
  const products = originalData.applicationProducts;
  if (requestProducts) {
    const type = "products";
    returnMap['products'] = buildProductsCompareTable(products, requestProducts, type);
  } else if (products && products.length > 0) {
    products.forEach(prd => {
      prd["currentTierUuid"] = prd.tierUuid;
      prd["currentTierName"] = prd.tierName;
    });
    returnMap['products'] = products;
  }
  const apiGroups = originalData.applicationApiGroups;
  if (requestApiGroups && requestApiGroups.length > 0) {
    returnMap['apigroups'] = buildApisCompareTable(apiGroups, requestApiGroups);
  } else if (apiGroups && apiGroups.length > 0) {
    returnMap['apigroups'] = apiGroups;
  }

  const apiKeysValues = [];
  if (requestSubtype === "CREATE") {
    forEach(originalData.applicationApiKeys, item => {
      apiKeysValues.push(...buildKeyDetailsData(undefined, item, true, requestSubtype));
    });
  } else if (requestSubtype === "UPDATE") {
    // If a request to edit the default key is made using the old OData end point
    // which takes old payload, then the updateRequest contains
    // only the updated default key, not all keys as in CREATE and DELETE request.
    // As the other keys remain same, copying them from originalData to updatedKeys,
    // so that we can show all the keys as we do for CREATE and DELETE flows
    const originalKeys = originalData.applicationApiKeys;
    const updatedKeys = cloneDeep(originalKeys);
    const updatedKeysFromRequest = updateRequest.applicationApiKeys;
    forEach(updatedKeysFromRequest, item => {
      const index = findIndex(updatedKeys, key => key.apiKey === item.apiKey);
      updatedKeys[index] = cloneDeep(item);
    });
    forEach(originalKeys, (item, index) => {
      apiKeysValues.push(...buildKeyDetailsData(item, updatedKeys[index], true, requestSubtype));
    });
  } else {
    forEach(originalData.applicationApiKeys, item => {
      apiKeysValues.push(...buildKeyDetailsData(item, undefined, true, requestSubtype));
    });
  }
  if(apiKeysValues?.length) {
    returnMap['apikeys'] = apiKeysValues;
  }
  const certificates = originalData.applicationCertificates;
  if (certificates && certificates.length > 0) {
    const certMap = originalData.applicationCertificates.map(cert => {
      return { name: cert.name, expiryTs: cert.expiryTs };
    });
    returnMap['certificates'] = certMap;
  }

  const oauthClients = [];
  if (requestSubtype === "CREATE") {
    forEach(originalData.applicationOAuthClients, item => {
      oauthClients.push(...buildOAuthKeyDetailsData(
        undefined, item, true, requestSubtype, requestDetails.request,
      ));
    });
  } else if (requestSubtype === "UPDATE") {
    // If a request to edit the default key is made using the old OData end point
    // which takes old payload, then the updateRequest contains
    // only the updated default key, not all keys as in CREATE and DELETE request.
    // As the other keys remain same, copying them from originalData to updatedKeys,
    // so that we can show all the keys as we do for CREATE and DELETE flows
    const originalKeys = originalData.applicationOAuthClients;
    const updatedKeys = cloneDeep(originalKeys);
    const updatedKeysFromRequest = updateRequest.applicationOAuthClients;
    forEach(updatedKeysFromRequest, item => {
      const index = findIndex(updatedKeys, key => key.apiKey === item.apiKey);
      updatedKeys[index] = cloneDeep(item);
    });
    forEach(originalKeys, (item, index) => {
      oauthClients.push(...buildOAuthKeyDetailsData(
        item, updatedKeys[index], true, requestSubtype, requestDetails.request,
      ));
    });
  } else {
    forEach(originalData.applicationOAuthClients, item => {
      oauthClients.push(...buildOAuthKeyDetailsData(
        item, undefined, true, requestSubtype, requestDetails.request,
      ));
    });
  }
  if(oauthClients?.length) {
    returnMap['oauthclients'] = oauthClients;
  }
  return returnMap;
};

const getLinkContent = (value, link) => (
  <Link underline="none" href={link}>
    {value}
  </Link>
);

export const buildRegistrationRequestTable = (request) => [{
  uuid: 'EMAIL',
  field: getI18nFormattedMessage('label.request.details.email.field.value'),
  value: request.entityName,
}, {
  uuid: 'ORGANIZATION',
  field: getI18nFormattedMessage('label.request.details.organization.field.value'),
  value: request.organizationName,
}, {
  uuid: 'ROLE',
  field: getI18nFormattedMessage('label.request.details.role.field.value'),
  value: getI18nFormattedMessage(USER_LIST_ROLE[ROLE_ORG_ADMIN_UUID]),
},
];
const formatApiStatus = (classes, status) => {
  const statusStyle = {
    fill: getStatusColor(status),
  };
  return (
    <span className={classes.statusField}>
      <FiberManualRecordIcon
        style={statusStyle} className={classes.statusIcon} />
      <span>{startCase(lowerCase(status))}</span>
    </span>);
}

const formatBold = (classes, value, testId) => {
  return (
    <span data-apim-test={testId} className={classes.boldField}>
      {value}
    </span>);
}

const getApiPlanContent = (planName, link, isAdmin) => isAdmin ?
  getLinkContent(planName, link) : planName;

const getAPIKeyFields = (isSingleRequest, classes, requestType) => {
  const apiKeysFields =
    [{
      id: 'field',
      label: getI18nFormattedMessage('label.request.details.section.field.column.title'),
      minWidth: 100,
      value: (item) => item && item.uuid && item.uuid.includes('KEY_NAME') ? formatBold(classes, item.field, `${item.uuid}_KEY_NAME`) : <>&nbsp;&nbsp;&nbsp;&nbsp;{item.field}</>,
    }];
  if (!isSingleRequest || requestType === 'APPLICATION_DELETE') {
    apiKeysFields.push({
      id: 'value',
      label: getI18nFormattedMessage(`label.request.details.section.${isSingleRequest ? '' : 'current.'}value.column.title`),
      minWidth: 100,
      value: (item) => item && item.uuid && item.uuid.includes('KEY_NAME') ? formatBold(classes, item.value) : <>{item.value}</>,
    });
  }
  if (requestType !== 'APPLICATION_DELETE') {
    apiKeysFields.push({
      id: 'requestValue',
      label: getI18nFormattedMessage('label.request.details.section.request.value.column.title'),
      minWidth: 100,
      value: (item) => item && item.uuid && item.uuid.includes('KEY_NAME') ? formatBold(classes, item.requestValue) : <>{item.requestValue}</>,
    });
  }
  return apiKeysFields;
};

export const getTableColumns =
  (classes, isSingleRequest, type, onClickValue, isAdmin, requestType, colCallback) => {
    switch (type) {
      case "details":
      case "customfields":
      case "status":
      case "addinfo":
      case "registration":
      case "drydetails": {
        const fields =
          [{
            id: 'field',
            label: getI18nFormattedMessage('label.request.details.section.field.column.title'),
            minWidth: 100,
            value: (item) => item.field,
          }, {
            id: 'value',
            label: getI18nFormattedMessage(`label.request.details.section.${isSingleRequest || type === "drydetails" ? '' : 'current.'}value.column.title`),
            minWidth: 100,
            value: (item) => {
              if (type === "registration") {
                return (<Button
                  className={classes.defaultButton}
                  data-apim-test="open-alert-dialog"
                  id="open-alert-dialog"
                  onClick={() => onClickValue()}
                  color="secondary"
                >
                  {item.value}
                  <EditIcon className={classes.editIcon} />
                </Button>)
              }
              return item.value;
            },
          }];
        if ((!isSingleRequest && type !== "drydetails") || type === "status") {
          fields.push({
            id: 'requestValue',
            label: getI18nFormattedMessage('label.request.details.section.request.value.column.title'),
            minWidth: 100,
            value: (item) => item.requestValue,
          });
        }
        return fields;
      }
      case "apikeys": {
        return getAPIKeyFields(isSingleRequest, classes, requestType);
      }
      case "apis":
      case "apigroups":
      case "apiplans": {
        const apiFields = [{
          id: 'name',
          label: getI18nFormattedMessage(`label.request.details.section.${type}.name.column.title`),
          minWidth: 200,
          value: (item) => getLinkContent(item.name,
            type === 'apis' || type === 'apiplans' ? `/publish/apis/details/${item.uuid}`
              : `/admin/api-groups/details/${item.uuid}`),
        }, {
          id: 'status',
          label: getI18nFormattedMessage('label.request.details.section.state.column.title'),
          minWidth: 100,
          value: (item) => formatApiStatus(classes, type === 'apis' || type === 'apiplans' ? item.portalStatus : item.status),
        }];
        if (type === "apiplans") {
          apiFields.push({
            id: 'apiPlan',
            label: getI18nFormattedMessage(`label.request.details.section.${isSingleRequest ? '' : 'current.'}apiplan.column.title`),
            minWidth: 100,
            value: (item) => getApiPlanContent(item.apiPlanName, `/admin/apis/api-plans/details/${item.apiPlanUuid}`, isAdmin),
          });
          if (!isSingleRequest) {
            apiFields.push({
              id: 'requestApiPlan',
              label: getI18nFormattedMessage('label.request.details.section.request.apiplan.column.title'),
              minWidth: 200,
              value: (item) => getApiPlanContent(item.requestApiPlanName, `/admin/apis/api-plans/details/${item.requestApiPlanUuid}`, isAdmin),
            });
          }
        }
        if (!isSingleRequest) {
          apiFields.push({
            id: 'request',
            label: getI18nFormattedMessage('label.request.details.section.request.column.title'),
            minWidth: 100,
            value: (item) => item.request,
          });
        }
        return apiFields;
      }
      case "products": {
        const productFields = [{
          id: 'name',
          label: getI18nFormattedMessage(`label.request.details.section.${type}.name.column.title`),
          minWidth: 200,
          value: (item) => getLinkContent(item.productName, `/admin/products/details/${item.productUuid}`),
        }];
        productFields.push({
          id: 'currentTierName',
          label: getI18nFormattedMessage(`label.request.details.section.${isSingleRequest ? '' : 'current.'}tier.column.title`),
          minWidth: 100,
          value: (item) => getLinkContent(item.currentTierName, `/admin/products/details/${item.productUuid}/ratequota`),
        });
        if (!isSingleRequest) {
          productFields.push({
            id: 'requestTierName',
            label: getI18nFormattedMessage('label.request.details.section.request.tier.column.title'),
            minWidth: 100,
            value: (item) => getLinkContent(item.requestTierName, `/admin/products/details/${item.productUuid}/ratequota`),
          });
          productFields.push({
            id: 'request',
            label: getI18nFormattedMessage('label.request.details.section.request.column.title'),
            minWidth: 100,
            value: (item) => item.request,
          });
        }
        return productFields;
      }
      case "certificates": {
        return getMultiRequestTableColumns(classes, isSingleRequest, type, isAdmin);
      }
      case "oauthclients": {
        return getOAuthKeyFields(isSingleRequest, classes, requestType, colCallback);
      }
    }
  }

export const getMultiRequestTableColumns =
  (classes, isSingleRequest, type, isAdmin, requestType, colCallback) => {
    if (type === 'certificates') {
      const certColumns = [{
        id: 'name',
        label: getI18nFormattedMessage(`label.request.details.section.${type}.name.column.title`),
        minWidth: 100,
        value: (item) => formatBold(classes, item.name),
      }, {
        id: 'expiryTs',
        label: getI18nFormattedMessage('label.request.details.section.certificates.expiry.date.column.title'),
        minWidth: 50,
        value: (item) => moment(item.expiryTs).format('MMM DD, YYYY'),
      }];
      if (!isSingleRequest) {
        certColumns.push({
          id: 'modifyTs',
          label: getI18nFormattedMessage('label.request.details.section.certificates.request.date.column.title'),
          minWidth: 50,
          value: (item) => moment(item.modifyTs).format('MMM DD, YYYY'),
        }, {
          id: 'modifiedBy',
          label: getI18nFormattedMessage('label.request.details.section.certificates.requester.column.title'),
          minWidth: 50,
          value: (item) => isAdmin ?
            (<Link href={`/admin/console/users/details/${item.userUuid}`}>
              {item.modifiedBy}
            </Link>) :
            (<span>
              {item.modifiedBy}
            </span>),
        }, {
          id: 'request',
          label: getI18nFormattedMessage('label.request.details.section.request.change.column.title'),
          minWidth: 50,
          value: (item) => item.requestSubType === 'DELETE' ? getI18nFormattedMessage('label.request.details.section.certificates.removed')
            : getI18nFormattedMessage('label.request.details.section.certificates.added'),
        });
      }
      return certColumns;
    } else if (type === 'products') {
      const productFields = [{
        id: 'name',
        label: getI18nFormattedMessage(`label.request.details.section.${type}.name.column.title`),
        minWidth: 200,
        value: (item) => getLinkContent(item.productName, `/admin/products/details/${item.productUuid}`),
      }];
      productFields.push({
        id: 'currentTierName',
        label: getI18nFormattedMessage(`label.request.details.section.${isSingleRequest ? '' : 'current.'}tier.column.title`),
        minWidth: 100,
        value: (item) => getLinkContent(item.currentTierName, `/admin/products/details/${item.productUuid}/ratequota`),
      });
      if (!isSingleRequest) {
        productFields.push({
          id: 'requestTierName',
          label: getI18nFormattedMessage('label.request.details.section.requested.tier.column.title'),
          minWidth: 50,
          value: (item) => getLinkContent(item.requestTierName, `/admin/products/details/${item.productUuid}/ratequota`),
        });
        productFields.push({
          id: 'request',
          label: getI18nFormattedMessage('label.request.details.section.request.column.title'),
          minWidth: 50,
          value: (item) => item.request,
        });
      }
      return productFields;
    } else if (type === 'apikeys') {
      return getAPIKeyFields(isSingleRequest, classes);
    } else if (type === 'oauthclients') {
      return getOAuthKeyFields(isSingleRequest, classes, requestType, colCallback);
    } else {
      const apiFields = [{
        id: 'name',
        label: getI18nFormattedMessage(`label.request.details.section.${type}.name.column.title`),
        minWidth: 100,
        value: (item) => getLinkContent(item.name,
          type === 'apis' || type === 'apiplans' ? `/publish/apis/details/${item.uuid}`
            : `/admin/api-groups/details/${item.uuid}`),
      }, {
        id: 'status',
        label: getI18nFormattedMessage('label.request.details.section.state.column.title'),
        minWidth: 200,
        value: (item) => formatApiStatus(classes, type === 'apis' || type === 'apiplans' ? item.portalStatus : item.status),
      }];
      if (type === "apiplans") {
        apiFields.push({
          id: 'currentApiPlan',
          label: getI18nFormattedMessage(`label.request.details.section.${isSingleRequest ? '' : 'current.'}apiplan.column.title`),
          minWidth: 100,
          value: (item) => getApiPlanContent(item.currentApiPlanName, `/admin/apis/api-plans/details/${item.currentApiPlanUuid}`, isAdmin),
        });
        if (!isSingleRequest) {
          apiFields.push({
            id: 'requestApiPlan',
            label: getI18nFormattedMessage('label.request.details.section.request.apiplan.column.title'),
            minWidth: 100,
            value: (item) => getApiPlanContent(item.requestApiPlanName, `/admin/apis/api-plans/details/${item.requestApiPlanUuid}`, isAdmin),
          });
        }
      }
      if (!isSingleRequest) {
        apiFields.push({
          id: 'request',
          label: getI18nFormattedMessage('label.request.details.section.request.column.title'),
          minWidth: 100,
          value: (item) => item.request,
        });
      }
      return apiFields;
    }
  }

export const buildDetailsData = (classes, applicationDetails, requestAppDetails, infoTable) => [{
  uuid: "NAME",
  field: getI18nFormattedMessage('label.request.details.section.field.appname'),
  value: applicationDetails.name,
  requestValue: requestAppDetails && requestAppDetails.name,
  selected: requestAppDetails ? applicationDetails.name !== requestAppDetails.name : false,
}, {
  uuid: "DESCRIPTION",
  field: <div className={classes.descriptionField}>
    {getI18nFormattedMessage('label.request.details.section.field.appdesc')}
    {!infoTable &&
      <Tooltip
        title={getI18nFormattedMessage('label.request.details.section.field.description.tooltip')}
        arrow placement="right">
        <HelpIcon className={classes.helpIcon} />
      </Tooltip>
    }
  </div>,
  value: applicationDetails.description,
  requestValue: requestAppDetails && requestAppDetails.description,
  selected: requestAppDetails ?
    applicationDetails.description !== requestAppDetails.description : false,
}];

const buildCustomFieldsData = (customFields) => customFields.map(cf => {
  return {
    uuid: cf.customFieldUuid,
    field: cf.name,
    value: cf.value,
  };
});

export const buildEditRequestTable = (classes, requestDetails) => {
  if (requestDetails) {
    const originalData = requestDetails.original;
    const requestData = requestDetails.updateRequest;
    let dataTable;
    let type;
    if (requestDetails.request.requestType === 'APPLICATION_OAUTH_CLIENTS') {
      type = 'oauthclients';
      dataTable = [];
      const subType = requestDetails.request.requestSubtype;
      if (subType === "DELETE") {
        forEach(originalData.applicationOAuthClients, (item, index) => {
          const requestDataKeys = (requestData && requestData.applicationOAuthClients) || [];
          const requestKey =
            requestDataKeys.length > index ? requestDataKeys[index] : {};
          const result = buildOAuthKeyDetailsData(item, requestKey, false, subType);
          dataTable.push(...result);
        });
      } else {
        forEach(requestData.applicationOAuthClients, (item, index) => {
          const originalDataKeys = (originalData && originalData.applicationOAuthClients) || [];
          const originalKey =
            originalDataKeys.length > index ? originalDataKeys[index] : {};
          const isUpdateRequest = subType === 'UPDATE';
          let result = buildOAuthKeyDetailsData(
            originalKey, item, false, subType, requestDetails.request,
          );
          if (isUpdateRequest) {
            result = map(result, entry => {
              entry['selected'] = (entry.value !== entry.requestValue)
              return entry;
            });
          }
          dataTable.push(...result);
        });
      }
    } else if (requestDetails.request.requestType === 'APPLICATION_API_KEYS') {
      type = 'apikeys';
      dataTable = [];
      const subType = requestDetails.request.requestSubtype;
      if (subType === "DELETE") {
        forEach(originalData.applicationApiKeys, (item, index) => {
          const requestDataKeys = (requestData && requestData.applicationApiKeys) || [];
          const requestKey =
            requestDataKeys.length > index ? requestDataKeys[index] : {};
          const result = buildKeyDetailsData(item, requestKey, false, subType);
          dataTable.push(...result);
        });
      } else {
        forEach(requestData.applicationApiKeys, (item, index) => {
          const originalDataKeys = (originalData && originalData.applicationApiKeys) || [];
          const originalKey =
            originalDataKeys.length > index ? originalDataKeys[index] : {};
          const isUpdateRequest = subType === 'UPDATE';
          let result = buildKeyDetailsData(originalKey, item, false, subType);
          if (isUpdateRequest) {
            result = map(result, entry => {
              entry['selected'] = (entry.value !== entry.requestValue)
              return entry;
            });
          }
          dataTable.push(...result);
        });
      }
    } else if (originalData && requestData) {
      switch (requestDetails.request.requestType) {
        case 'APPLICATION_DETAILS':
          if (requestDetails.request.requestSubtype === 'APP_STATUS_CHANGE') {
            type = 'status';
            dataTable = buildStatusChangeDataTable(
              requestDetails.request.previousState, requestData.applicationDetails.status);
          } else {
            type = 'details';
            dataTable = buildDetailsData(
              classes, originalData.applicationDetails, requestData.applicationDetails);
          }
          break;
        case 'APPLICATION_CUSTOM_FIELDS':
          type = "customfields";
          dataTable = buildCustomFieldsCompareTable(
            originalData.applicationCustomFields, requestData.applicationCustomFields);
          break;
        case 'APPLICATION_APIS':
        case 'APPLICATION_API_PLANS':
          type = requestDetails.request.requestType === "APPLICATION_API_PLANS" ? "apiplans" : "apis";
          dataTable =
            buildApisCompareTable(originalData.applicationApis, requestData.applicationApis, type);
          break;
        case 'APPLICATION_PRODUCTS':
          type = 'products'
          dataTable =
            buildProductsCompareTable(
              originalData.applicationProducts, requestData.applicationProducts);
          break;
        case 'APPLICATION_API_GROUPS':
          type = "apigroups";
          dataTable =
            buildApisCompareTable(
              originalData.applicationApiGroups, requestData.applicationApiGroups, type);
          break;
      }
    }
    return {
      type,
      dataTable,
    };
  }
};

export const buildEditApisRequestTable = (requestDetails, hasApiPlanEnabled) => {
  if (requestDetails) {
    const type = hasApiPlanEnabled ? "apiplans" : "apis";
    const dataTable = buildApisTable(requestDetails, type);
    return { type, dataTable };
  }
}

export const buildEditProductsRequestTable = (requestDetails) => {
  if (requestDetails) {
    const type = 'products';
    const dataTable = buildProductsTable(requestDetails, type);
    return { type, dataTable };
  }
}

const buildCustomFieldsCompareTable = (originalCustomFields, requestCustomFields) => {
  const customFieldsMerged = {};
  originalCustomFields.forEach(cf => {
    customFieldsMerged[cf.customFieldUuid] = {
      uuid: cf.customFieldUuid,
      field: cf.name,
      value: cf.value,
    };
  });
  requestCustomFields.forEach(cf => {
    if (Object.keys(customFieldsMerged).includes(cf.customFieldUuid)) {
      customFieldsMerged[cf.customFieldUuid].requestValue = cf.value;
    } else {
      customFieldsMerged[cf.customFieldUuid] = {
        uuid: cf.customFieldUuid,
        field: cf.name,
        requestValue: cf.value,
      }
    }
  });
  const cfMergedList = Object.values(customFieldsMerged);
  cfMergedList.forEach(cf => cf['selected'] = cf.value !== cf.requestValue);
  return cfMergedList;
};

const buildApisCompareTable = (originalApis, requestApis, type) => {
  const originalApiKeys = originalApis.map(api => api.uuid);
  const requestApisMap = {};
  requestApis.forEach(api => requestApisMap[api.uuid] = api);
  requestApis.forEach(api => {
    if (!originalApiKeys.includes(api.uuid)) {
      api["request"] = getI18nFormattedMessage('label.request.details.section.api.added');
      api['selected'] = true;
      originalApis.push(api);
    }
  });
  originalApis.forEach(api => {
    if (Object.keys(requestApisMap).includes(api.uuid)) {
      if (type === "apiplans") {
        const reqApi = requestApisMap[api.uuid];
        api["requestApiPlanUuid"] = reqApi.apiPlanUuid;
        api["requestApiPlanName"] = reqApi.apiPlanName;
        if (api.selected) {
          api["apiPlanUuid"] = null;
          api["apiPlanName"] = null;
        }
        if (reqApi.apiPlanUuid !== api.apiPlanUuid) {
          api['selected'] = true;
          api['request'] = getI18nFormattedMessage('label.request.details.section.api.plan.changed');
        }
      }
    } else {
      api["request"] = getI18nFormattedMessage('label.request.details.section.api.removed');
      api['selected'] = true;
    }
  });
  return originalApis;
};

const buildProductsCompareTable = (originalProducts, requestProducts) => {
  const originalPrds = originalProducts.map(prd => prd.productUuid);
  const requestProductsMap = {};
  requestProducts.forEach(prd => requestProductsMap[prd.productUuid] = prd);
  requestProducts.forEach(prd => {
    if (!originalPrds.includes(prd.productUuid)) {
      prd["request"] = getI18nFormattedMessage('label.request.details.section.product.added');
      prd['selected'] = true;
      originalProducts.push(prd);
    }
  });
  originalProducts.forEach(prd => {
    prd["currentTierUuid"] = prd.tierUuid;
    prd["currentTierName"] = prd.tierName;
    if (Object.keys(requestProductsMap).includes(prd.productUuid)) {
      const reqProduct = requestProductsMap[prd.productUuid];
      if (reqProduct.tierUuid !== prd.tierUuid) {
        prd["requestTierUuid"] = reqProduct.tierUuid;
        prd["requestTierName"] = reqProduct.tierName;
        prd['selected'] = true;
        prd['request'] = getI18nFormattedMessage('label.request.details.section.product.tier.changed');
      } else {
        prd["request"] = getI18nFormattedMessage('label.request.details.section.api.removed');
        prd['selected'] = true;
      }
    }
  });
  return originalProducts;
};

const buildApisTable = (requestDetails, type) => {
  //requestDetails: array contails all requests
  const requestsRowApiMap = [];
  requestDetails.map(reqData => {
    // get requestedApis from request details
    const requestApis = get(reqData, 'updateRequest.applicationApis') || [];
    requestApis.map(reqApi => {
      const originalApis = get(reqData, 'original.applicationApis') || [];
      const orginalApi = originalApis.find(orgApi => orgApi.uuid === reqApi.uuid);

      //added this condtion for put call check
      if (reqData.request.requestSubtype === 'UPDATE' || reqData.request.requestSubtype === null) {
        if (orginalApi) {
          if (type === "apiplans") {
            if (reqApi.apiPlanUuid !== get(orginalApi, 'apiPlanUuid')) {
              reqApi['request'] = getI18nFormattedMessage('label.request.details.section.api.plan.changed');
            } else {
              reqApi['request'] = getI18nFormattedMessage('label.request.details.section.api.removed');
            }
          }
        } else {
          reqApi['request'] = getI18nFormattedMessage('label.request.details.section.api.added');
        }
      } else if (reqData.request.requestSubtype === 'REMOVE') {
        reqApi['request'] = getI18nFormattedMessage('label.request.details.section.api.removed');
      } else {
        reqApi['request'] = getI18nFormattedMessage('label.request.details.section.api.added');
      }
      if (type === 'apiplans') { // check the type
        reqApi['requestApiPlanUuid'] = reqApi.apiPlanUuid;
        reqApi['requestApiPlanName'] = reqApi.apiPlanName;
        if (orginalApi) {
          reqApi['currentApiPlanName'] = get(orginalApi, 'apiPlanName');
          reqApi['currentApiPlanUuid'] = get(orginalApi, 'apiPlanUuid');
          if (reqData.request.requestSubtype === 'UPDATE') {
            reqApi['request'] = getI18nFormattedMessage('label.request.details.section.api.plan.changed');
          }
        }
      }
    });
    reqData.request['requestEntities'] = requestApis;
    requestsRowApiMap.push(reqData.request);
  });
  return requestsRowApiMap;
};

const buildProductsTable = (requestDetails) => {
  //requestDetails: array contails all requests
  const requestsRowProductMap = [];
  requestDetails.map(reqData => {
    // get requestedApis from request details
    const requestProducts = get(reqData, 'updateRequest.applicationProducts') || [];
    requestProducts.map(reqPrd => {
      const originalProducts = get(reqData, 'original.applicationProducts') || [];
      const orginalProduct = originalProducts.find(prd => prd.productUuid === reqPrd.productUuid);
      //added this condtion for put call check
      if (reqData.request.requestSubtype === 'UPDATE' || reqData.request.requestSubtype === null) {
        if (orginalProduct) {
          if (reqPrd.tierUuid !== get(orginalProduct, 'tierUuid')) {
            reqPrd['currentTierName'] = get(orginalProduct, 'tierName');
            reqPrd['currentTierUuid'] = get(orginalProduct, 'tierUuid');
            reqPrd['requestTierUuid'] = reqPrd.tierUuid;
            reqPrd['requestTierName'] = reqPrd.tierName;
            reqPrd['request'] = getI18nFormattedMessage('label.request.details.section.product.tier.changed');
          } else {
            reqPrd['currentTierName'] = get(orginalProduct, 'tierName');
            reqPrd['currentTierUuid'] = get(orginalProduct, 'tierUuid');
            reqPrd['request'] = getI18nFormattedMessage('label.request.details.section.api.removed');
          }
        } else {
          reqPrd['requestTierUuid'] = reqPrd.tierUuid;
          reqPrd['requestTierName'] = reqPrd.tierName;
          reqPrd['request'] = getI18nFormattedMessage('label.request.details.section.api.added');
        }
      } else if (reqData.request.requestSubtype === 'REMOVE') {
        reqPrd['currentTierName'] = get(orginalProduct, 'tierName');
        reqPrd['currentTierUuid'] = get(orginalProduct, 'tierUuid');
        reqPrd['request'] = getI18nFormattedMessage('label.request.details.section.api.removed');
      } else {
        reqPrd['requestTierUuid'] = reqPrd.tierUuid;
        reqPrd['requestTierName'] = reqPrd.tierName;
        reqPrd['request'] = getI18nFormattedMessage('label.request.details.section.api.added');
      }
    });
    reqData.request['requestEntities'] = requestProducts;
    requestsRowProductMap.push(reqData.request);
  });
  return requestsRowProductMap;
};

const buildStatusChangeDataTable = (prevStatus, reqStatus) => [{
  uuid: 'STATUS',
  field: getI18nFormattedMessage('label.request.details.section.field.status'),
  value: startCase(lowerCase(prevStatus)),
  requestValue: startCase(lowerCase(reqStatus)),
  selected: prevStatus !== reqStatus,
}];

export const buildCertificatesData = (requestDetails) => {
  const appCert = get(requestDetails, 'original.applicationCertificates')[0];
  const requestUuid = get(requestDetails, 'request.uuid');
  const requestSubType = get(requestDetails, 'request.requestSubtype');
  return {
    uuid: `${requestUuid}`,
    type: 'certificates',
    requestType: 'APPLICATION_CERTIFICATES',
    selected: true,
    modifyTs: get(requestDetails, 'request.modifyTs'),
    modifiedBy: get(requestDetails, 'request.modifiedBy'),
    userUuid: get(requestDetails, 'request.userUuid'),
    name: appCert.name,
    expiryTs: appCert.expiryTs,
    requestSubType: requestSubType,
  }
};

const buildKeyDetailsData =
  (apiKeyObj = {}, requestedKeyObj = {}, isAppCreateEditDeleteRequest, subType) => {
    const hasOriginalData = (subType === 'UPDATE') || (subType === 'DELETE');
    const hasRequestedData = (subType === 'CREATE') || (subType === 'UPDATE');
    const apiKey = (subType === 'DELETE') ? apiKeyObj && apiKeyObj.apiKey : requestedKeyObj && requestedKeyObj.apiKey;
    let baseObj = (subType === 'DELETE') ? apiKeyObj : requestedKeyObj;
    if (isAppCreateEditDeleteRequest && subType === 'UPDATE') {
      baseObj = apiKeyObj;
    }
    const apiKeyRows =
      [{
        uuid: `KEY_NAME_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.keyname'),
        value: apiKeyObj && apiKeyObj.name,
        requestValue: requestedKeyObj && requestedKeyObj.name,
      }, {
        uuid: `KEY_IS_DEFAULT_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.isdefault'),
        value: hasOriginalData ? (apiKeyObj && apiKeyObj.defaultKey ? "true" : "false") : "",
        requestValue: hasRequestedData ? (requestedKeyObj && requestedKeyObj.defaultKey ? "true" : "false") : "",
      }, {
        uuid: `KEY_STATUS_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.status'),
        value: apiKeyObj && apiKeyObj.status,
        requestValue: requestedKeyObj && requestedKeyObj.status,
      }, {
        uuid: `KEY_AUTH_METHOD_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.authmethod'),
        value: apiKeyObj && apiKeyObj.authMethod,
        requestValue: requestedKeyObj && requestedKeyObj.authMethod,
      }];
    if (baseObj && (baseObj.authMethod === AUTH_METHOD_SECRET
      || baseObj.authMethod === AUTH_METHOD_NONE)) {
      apiKeyRows.push({
        uuid: `KEY_OAUTH_URL_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.oauth.url'),
        value: apiKeyObj && apiKeyObj.oauthCallbackUrl,
        requestValue: requestedKeyObj && requestedKeyObj.oauthCallbackUrl,
        visible: baseObj.authMethod === AUTH_METHOD_SECRET,
      }, {
        uuid: `KEY_OAUTH_SCOPE_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.oauth.scope'),
        value: apiKeyObj && apiKeyObj.oauthScope,
        requestValue: requestedKeyObj && requestedKeyObj.oauthScope,
        visible: baseObj.authMethod === AUTH_METHOD_SECRET,
      }, {
        uuid: `KEY_OAUTH_TYPE_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.oauth.type'),
        value: apiKeyObj && apiKeyObj.oauthType,
        requestValue: requestedKeyObj && requestedKeyObj.oauthType,
        visible: baseObj.authMethod === AUTH_METHOD_SECRET,
      });
    }
    if (baseObj && (baseObj.authMethod === AUTH_METHOD_NONE)) {
      apiKeyRows.push({
        uuid: `KEY_AUTH_PROVIDER_NAME_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.authprovider.name'),
        value: apiKeyObj && apiKeyObj.authProviderName,
        requestValue: requestedKeyObj && requestedKeyObj.authProviderName,
        visible: baseObj.authMethod === AUTH_METHOD_NONE,
      }, {
        uuid: `KEY_AUTH_PROVIDER_ISSUER_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.authprovider.issuer'),
        value: apiKeyObj && apiKeyObj.authProviderIssuer,
        requestValue: requestedKeyObj && requestedKeyObj.authProviderIssuer,
        visible: baseObj.authMethod === AUTH_METHOD_NONE,
      });
    }
    apiKeyRows.push({
      uuid: `KEY_API_KEY_${apiKey}`,
      field: getI18nFormattedMessage('label.request.details.section.field.apikey'),
      value: apiKeyObj && apiKeyObj.apiKey,
      requestValue: requestedKeyObj && requestedKeyObj.apiKey,
    });
    if (baseObj && (baseObj.authMethod === AUTH_METHOD_SECRET)) {
      apiKeyRows.push({
        uuid: `KEY_SECRET_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.secret'),
        value: apiKeyObj && apiKeyObj.keySecret,
        requestValue: requestedKeyObj && requestedKeyObj.keySecret,
      });
    }
    return apiKeyRows;
  }

const formatLinkWithCallback = (classes, testId, value, colCallback) => {
  if (value?.length > 100) {
    return (
      <Fragment>
        <span>{value.substring(0, 99)}</span>
        <Tooltip title={getI18nFormattedMessage('label.request.details.section.field.oauth.client.payload.tooltip')}>
          <Link data-apim-test={testId}
            onClick={() => colCallback(value)}
            className={classes.linkFieldContainer}
          >
            {getI18nFormattedMessage(
              'label.application.details.keys.auth.provider.client.definition.expand',
            )}
          </Link>
        </Tooltip>
      </Fragment>
    );
  } else {
    return (
      <span>{value}</span>
    );
  }
}
const buildOAuthKeyDetailsData =
  (apiKeyObj = {}, requestedKeyObj = {}, isAppCreateEditDeleteRequest, subType) => {
    const hasOriginalData = (subType === 'UPDATE') || (subType === 'DELETE');
    const hasRequestedData = (subType === 'CREATE') || (subType === 'UPDATE');
    const apiKey = (subType === 'DELETE') ? apiKeyObj && apiKeyObj.name : requestedKeyObj && requestedKeyObj.name;
    let baseObj = (subType === 'DELETE') ? apiKeyObj : requestedKeyObj;
    if (isAppCreateEditDeleteRequest && subType === 'UPDATE') {
      baseObj = apiKeyObj;
      baseObj;
    }
    const data = [{
      uuid: `KEY_NAME_${apiKey}`,
      field: getI18nFormattedMessage('label.request.details.section.field.oauth.client.title'),
      value: apiKeyObj && apiKeyObj.name,
      requestValue: requestedKeyObj && requestedKeyObj.name,
    }, {
      uuid: `KEY_IS_DEFAULT_${apiKey}`,
      field: getI18nFormattedMessage('label.request.details.section.field.isdefault'),
      value: hasOriginalData ? apiKeyObj && apiKeyObj.defaultKey ? "true" : "false" : '',
      requestValue: hasRequestedData ? (requestedKeyObj && requestedKeyObj.defaultKey ? "true" : "false") : "",
    }, {
      uuid: `KEY_STATUS_${apiKey}`,
      field: getI18nFormattedMessage('label.request.details.section.field.status'),
      value: apiKeyObj && apiKeyObj.status && getStatus(apiKeyObj.status),
      requestValue: requestedKeyObj && requestedKeyObj.status && getStatus(requestedKeyObj.status),
    }, {
      uuid: `KEY_AUTH_PROVIDER_${apiKey}`,
      field: getI18nFormattedMessage('label.request.details.section.field.auth.provider'),
      value: apiKeyObj && apiKeyObj.authProviderName,
      requestValue: requestedKeyObj && requestedKeyObj.authProviderName,
    }, {
      uuid: `KEY_AUTH_PROVIDER_ISSUER_${apiKey}`,
      field: getI18nFormattedMessage('label.request.details.section.field.auth.provider.issuer'),
      value: apiKeyObj && apiKeyObj.authProviderIssuer,
      requestValue: requestedKeyObj && requestedKeyObj.authProviderIssuer,
    }, {
      uuid: `KEY_PAYLOAD_DEFN_${apiKey}`,
      field: getI18nFormattedMessage('label.auth.providers.details.sample.client.definition.metadata'),
      value: apiKeyObj && JSON.stringify(apiKeyObj.clientMetadata, null, 2),
      requestValue: requestedKeyObj && JSON.stringify(requestedKeyObj.clientMetadata,
        null, 2),
    },
    ];
    if (subType !== 'CREATE' && statusNotPending(apiKeyObj?.clientId)) {
      data.push({
        uuid: `KEY_CLIENT_ID_${apiKey}`,
        field: getI18nFormattedMessage('label.request.details.section.field.auth.provider.oauthclient'),
        value: apiKeyObj && apiKeyObj.clientId,
        requestValue: requestedKeyObj && requestedKeyObj.clientId,
      });
    }
    return data;
  }

const getOAuthKeyFields = (isSingleRequest, classes, requestType, colCallback) => {
  const oauthClients =
    [{
      id: 'field',
      label: getI18nFormattedMessage('label.request.details.section.field.column.title'),
      minWidth: 200,
      value: (item) => item && item.uuid && item.uuid.includes('KEY_NAME') ? formatBold(classes, item.field, `${item.uuid}_KEY_NAME`) : <>&nbsp;&nbsp;&nbsp;&nbsp;{item.field}</>,
    }];
  if (!isSingleRequest || requestType === 'APPLICATION_DELETE') {
    oauthClients.push({
      id: 'value',
      label: getI18nFormattedMessage(`label.request.details.section.${isSingleRequest ? '' : 'current.'}value.column.title`),
      minWidth: 200,
      value: (item) => item && item.uuid && item.uuid.includes('KEY_PAYLOAD_DEFN') ? formatLinkWithCallback(classes, `${item.name}_payload`, item.value, colCallback) : item.uuid.includes('KEY_NAME') ? formatBold(classes, item.value) : <>{item.value}</>,
    });
  }
  if (requestType !== 'APPLICATION_DELETE') {
    oauthClients.push({
      id: 'requestValue',
      label: getI18nFormattedMessage('label.request.details.section.request.value.column.title'),
      minWidth: 200,
      value: (item) => item && item.uuid && item.uuid.includes('KEY_PAYLOAD_DEFN') ? formatLinkWithCallback(classes, `${item.name}_requested_payload`, item.requestValue, colCallback) : item.uuid.includes('KEY_NAME') ? formatBold(classes, item.requestValue) : <>{item.requestValue}</>,
    });
  }
  return oauthClients;
}

const getStatus = (status) => status ? capitalCase(status):status;
const statusNotPending = (clientId="") => !clientId?.toLowerCase().startsWith('pending');
